import React, { useEffect, useRef, useLayoutEffect } from "react";
import styled, { createGlobalStyle } from "styled-components";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/* ===============================================================
GLOBAL STYLES & HELPERS
=============================================================== */
const GlobalStyles = createGlobalStyle`
  *,*::before,*::after{box-sizing:border-box;}
  html{scroll-behavior:smooth;}
  body{margin:0;padding:0;background:#efefef;}
  @font-face{
    font-family:'AnybodyMedium';
    src:url('../assets/fonts/Anybody-Medium.ttf') format('truetype');
    font-weight:500;font-style:normal;font-display:swap;
  }
`;

const breakpoints = {mobile:"40rem", tablet:"64rem"};

/* ===============================================================
LAYOUT WRAPPERS
=============================================================== */
const Page = styled.article`
  width:100%;min-width:0;background:#efefef;font-family:"Anybody",sans-serif;color:#000;overflow-x:hidden;
`;

const LilSpace = styled.div`height:6vh;`;

/* ===============================================================
HERO SEQUENCE
=============================================================== */
const HeroWrapper = styled.section`
  position:relative;
  height: var(--hero-height);  
`;

const Headline = styled.h1`
  position:sticky;top:0;display:flex;flex-direction:column;justify-content:center;align-items:center;
  margin:0;padding:clamp(4rem,16vh,20rem) 0;text-align:center;line-height:.9;color:#1388F9;
  font-family:'Anybody',sans-serif;font-weight:900;
  span{font-size:clamp(2.4rem,8.5vw,8rem);white-space:nowrap;}
`;

const FlyingImg = styled.img`
  position:absolute;z-index:1;left:50%;top:clamp(12rem,24vh,30rem);
  width:clamp(26rem,118vw,120rem);transform:translateX(-50%);
  will-change:transform,opacity;pointer-events:none;
`;

const InfoText = styled.div`
  /* ─── desktop  +  tablet-landscape ─── */
  position: absolute;
  left: clamp(7rem, 6vw, 8rem);
  top:  calc(var(--info-top) - 52rem);

  max-width: 48rem;
  color: #1388F9;
  font-family: 'Anybody', sans-serif;
  font-weight: 900;
  line-height: 1.3;

  p {
    margin: 0 0 1rem;
    font-size: clamp(.875rem, 2.4vw, 1.5rem);
  }

  /* ─── phones (≤40 rem) – keep your old centred style ─── */
  @media (max-width: ${breakpoints.mobile}) {
    top: calc(var(--info-top) - 10rem);
    left: 50%;
    transform: translateX(-50%);
    text-align: center;
    max-width: 90%;
  }

  /* ─── tablet-portrait (40–64 rem & portrait) – also centred ─── */
  @media (min-width: ${breakpoints.mobile}) and (max-width: ${breakpoints.tablet}) and (orientation: portrait) {
    top: calc(var(--info-top) - 18rem);
    left: 20%;
    transform: translateX(-50%);
    text-align: center;
    max-width: 90%;
  }
`;

/* ===============================================================
SPINNING ROW (unchanged)
=============================================================== */
// in your SpinRowWrapper definition, right after the existing mobile rules:ё
const SpinImg = styled.img`
  width: ${({ $big }) =>
    $big
      ? "clamp(22rem,46vw,40rem)"
      : "clamp(10rem,26vw,22rem)"};
  margin: 0 -5rem;
  flex-shrink: 0;
  will-change: transform, opacity;

  @media (max-width: ${breakpoints.mobile}) {
    width: ${({ $big }) =>
      $big
        ? "clamp(18rem,40vw,28rem) !important"
        : "clamp(12rem,20vw,16rem) !important"};
    margin: 0 -4rem !important;
  }
`;

const SpinRowWrapper = styled.section`
  position: relative;
  min-height: 80vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  margin-bottom: 0;

  @media (max-width: ${breakpoints.mobile}) {
    min-height: 20vh !important;
    margin-bottom: 0.1rem !important;

    /* bump the first image up a bit */
    ${SpinImg}:first-child {
      margin-top: -1.3rem !important;
    }
  }
`;


// Обновлён SpinImg: применяем !important внутри медиа-запроса


/* ===============================================================
TYPOGRAPHY HELPERS
=============================================================== */
const baseBodySize = "1.5rem";
const Para = styled.p`
  font-family:'AnybodyMedium',"Anybody",sans-serif;font-weight:500;
  font-size:clamp(.875rem,1.6vw + .3rem,${baseBodySize});line-height:1.38;
  max-width:80ch;margin:0 auto 1rem;text-align:${({$left})=>$left?"left":"center"};
`;
const Sub = styled.h3`
  font-weight:900;font-size:clamp(1.6rem,4.8vw,3.5rem);text-align:center;color:#1388F9;line-height:1.05;margin:3rem 0;
`;
const WideImg = styled.img`width:100%;height:auto;display:block;`;
const SpotifyFrame = styled.iframe`display:block;border-radius:12px;width:100%;height:30rem;max-width:80ch;margin:3rem auto;`;
const FooterBar = styled.footer`
  background:#000;padding:clamp(2rem,4vh,3rem) 0;text-align:center;
  h1{color:#fff;font-size:clamp(2.6rem,8vw,8rem);font-weight:900;margin:0;line-height:1;}
`;
const ImgRow = styled.div`
  display:grid;grid-template-columns:repeat(2,1fr);gap:clamp(0,2vw,2rem);grid-auto-rows:1fr;
  ${WideImg}{width:100%;height:100%;object-fit:cover;}
  @media(max-width:${breakpoints.mobile}){grid-template-columns:1fr;${WideImg}{height:auto;}}
`;



/* ===============================================================
SUB‑COMPONENTS
=============================================================== */
function HeroSequence(){
  const wrapper = useRef(null);const img = useRef(null);const info = useRef(null);

  useLayoutEffect(() => {
    const imgEl     = img.current;
    const wrapperEl = wrapper.current;
    const infoEl    = info.current;
    if (!imgEl || !wrapperEl || !infoEl) return;
  
    /* ───────── pull-up multiplier (tweak these) ───────── */
    const getOffset = () => {
      const vh = window.innerHeight;
      if (window.matchMedia(`(min-width:${breakpoints.tablet})`).matches) {
        return -vh * 0.03;        // desktop  gap  (- = smaller)
      }
      if (window.matchMedia(`(min-width:${breakpoints.mobile})`).matches) {
        return -vh * 0.005;        // tablet   gap
      }
      return -vh * 0.0001;          // mobile   gap
    };
  
    /* ───────── writer scheduled on rAF to avoid RO loop ───────── */
    let frameId = 0;
    const writeVars = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        /* --info-top */
        const imgRect     = imgEl.getBoundingClientRect();
        const wrapperRect = wrapperEl.getBoundingClientRect();
        const infoTop     = imgRect.bottom - wrapperRect.top + 24;
        wrapperEl.style.setProperty("--info-top", `${infoTop}px`);
  
        /* --hero-height */
        const naturalH = wrapperEl.scrollHeight;
        const heroH    = Math.max(naturalH + getOffset(), naturalH * 0.7);
        wrapperEl.style.setProperty("--hero-height", `${heroH}px`);
  
        ScrollTrigger.refresh();
      });
    };
  
    /* ───────── observe intrinsic-height changes ───────── */
    const ro = new ResizeObserver(writeVars);
  
    const init = () => {
      writeVars();                    // initial pass
      document.fonts?.ready.then(writeVars);
      ro.observe(wrapperEl);          // start observing
    };
  
    if (imgEl.complete && imgEl.naturalHeight) {
      init();
    } else {
      imgEl.addEventListener("load", init, { once: true });
    }
  
    window.addEventListener("resize", writeVars);
  
    /* ───────── cleanup ───────── */
    return () => {
      cancelAnimationFrame(frameId);
      ro.disconnect();
      imgEl.removeEventListener("load", init);
      window.removeEventListener("resize", writeVars);
    };
  }, []);
  
  
  

  useEffect(()=>{
    // 1) всегда ставим скролл на верх
    window.scrollTo(0, 0);
    // 2) пересчитываем все ScrollTrigger’ы
    ScrollTrigger.refresh();

    if(!wrapper.current) return;
    const ctx = gsap.context(()=>{
      const pinEnd = () => wrapper.current.offsetHeight;

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: wrapper.current,
          start:  'top top',
          end:    () => `+=${pinEnd()}`,
          scrub:  true,
          pin:    wrapper.current,
          pinSpacing: true,
        },
      });

      tl.fromTo(
        img.current,
        { yPercent: 80, opacity: 0 },
        { yPercent: 0,  opacity: 1, ease: 'power2.out', duration: 1 }
      );

      gsap.fromTo(
        info.current,
        { xPercent: -150, opacity: 0 },
        {
          xPercent: 0, opacity: 1, ease: 'power2.out', duration: 1,
          scrollTrigger: {
            trigger: wrapper.current,
            start:   'top+=15% top',
            end:     '+=50%',
            scrub:   true,
          },
        }
      );

      gsap.to(info.current, {
        yPercent: 75, ease: 'none',
        scrollTrigger: {
          trigger: wrapper.current,
          start:   'top top',
          end:     () => `+=${pinEnd()}`,
          scrub:   true,
        }
      });
    }, wrapper);

    return () => {
      ctx.revert();
    };
  }, []);

  


  return(
    <HeroWrapper ref={wrapper}>
      <Headline>
        <span>RICHARD BUTLER</span>
        <span>WELCOMES US</span>
        <span>INTO HIS BRAIN</span>
      </Headline>

      <FlyingImg ref={img} src= "/assets/flying.png" alt="Model in flight" />

      <InfoText ref={info}>
        <p>
          DESIGNER<br/>
          RICHARD BUTLER<br/>
          SEES THE FASHION HE CREATES<br/>
          THROUGH THE LENS OF HIS SYNAESTHESIA.<br/>
          GABRIEL WINTER MET UP WITH BUTLER FOR A<br/>
          COFFEE AND A CHAT ABOUT WHAT MAKES HIS<br/>
          VISIONS SO INTEGRAL TO HIS WORK.
        </p>
        <p>
          PHOTOGRAPHY: LAWRENCE ATKIN<br/>
          MAKEUP ARTIST: SASHA MAMEDOVA<br/>
          STYLIST: DAVID LAMMOND<br/>
          MODELS: CLEMENTINE MEALOR‑<br/>
          PRITCHARD, HANNAH HORNSBY,<br/>
          ELYSSA SAVILLE
        </p>
      </InfoText>
    </HeroWrapper>
  );
}

function SpinningRow(){
  return(
    <SpinRowWrapper >
      <SpinImg src="/assets/spinning-clem1.png" alt="Look 1" style={{marginTop:"-2rem"}} />
      <SpinImg src="/assets/spinning-clem2.png" alt="Look 2" $big />
      <SpinImg src="/assets/spinning-clem3.png" alt="Look 3" />
    </SpinRowWrapper >
  );
}

/* ===============================================================
   Component
   =============================================================== */
export default function Synesthesia() {
  return (
    <Page>
      <GlobalStyles />

      {/* ✨ Scroll‑driven hero */}
      <HeroSequence />
      {/* <Spacer />     */}
      {/* остальной текст статьи — без изменений */}
   
      {/* lead */}
      <section>
        <Para>
        As a child, I used to love watching the iTunes visualisers that danced to the syncopated rhythms of the songs as they played. Watching them gave me this multi-sensory experience that complemented the sounds of the music, and furthermore,        <br /> it was fun! 
        </Para>
        <Para>
        Synesthesia is the blending of senses, so in a way, synesthesia could be described as a built-in visualiser enabling creative individuals, to envision shapes, colours and movement from a sound stimulus such as music; and this is what has inspired my deep-dive into the world of synesthesia and its relationship with music and fashion. 
        </Para>
        <Para>
        "Before I went to Central Saint Martins, I spent many years working in the industry as a garment maker. I used this collection as an experiment to see if I could replicate my dream-like visions not only into reality but into wearable sculptures that could be walked in and even danced in," Butler states. , an aspect of his vision that RIFF Magazine wanted to bring to life through our photoshoot and Spotify embeds. 
Butler and spoke about my love for the iTunes visualiser that entranced me as a young boy, to which the creative responded, "That's exactly what I was trying to do with this collection!" The colours bouncing and shooting in every direction across the screen of the iMac visualisers were one of the first times an accompanying spectacle was so readily available to the masses, to any song you wanted.
This quintessential essence of a Central Saint Martins alumnus is apparent throughout Butler’s designs and begs the question: do his garments act as physical visualisers, born from his synesthesia? The answer is a pronounced yes.
Richard Butler presented his esteemed graduate collection, "Blackout/Fantasia" – inspired by the hit Britney Spears 2007 album, 'Blackout' – in 2024. The platinum-certified album was an immediate source of inspiration for the graduate’s divine collection. With bouncy synths, poppy vocals, and the sometimes over-edited qualities of the music allowed Butler to tap into his synesthesia and grow shapes, colours and textures from the sounds he heard. “Experiencing her album ‘Blackout’ was a pivotal moment for me because it was unlike anything I had ever heard before,” explains Butler, adding, “I had such a strong synesthetic reaction to it, more than anything I have experienced before or since.” The catalyst for the vibrancy shown throughout the lineup of pieces presented at his graduate show.
        </Para>
      </section>

      <SpinningRow />

      {/* body copy – part 2 */}
      <section>
        <Para>
        "I experience multiple types of synesthesia. The first one, the most common, is called Grapheme-colour Synesthesia, in which my brain associates different colours with certain numbers and letters. Another type is called Chromesthesia, where certain music and sounds evoke their own colours and shapes. The latter is what I wanted to explore with this collection," describes Butler. The collection consists of six individual looks, one relating to a certain song off the album. For Butler, the sentiments within each look are deeper than encompassing the whole track in one garment; in reality, the garments correspond to smaller aspects of each. "I used completely opaque fabrics to replicate the dominant bass-heavy sounds that you hear instantly on first listen. I used sheer fabrics to replicate those less dominant sounds higher in the register that may require further listening to really get." For example, the pulsating synth jabs of Gimme More were what popped the vibrant colours of Look One into the designer's mind, stating that "When I listen to music, I almost compulsively dissect the different layers in my head," separating the individual noises to craft certain aspects of the garment. On the contrary, in Look Four of his BA collection, the lowered vocals in Get Naked (I Got A Plan) symbolised the striking human face which protrudes from the tent-like structure, crafted from metal wires and translucent fabric. Due to the nature of the songs on the album, differentiating themselves from the others with their unique features and rhythms, the looks in Richard Butler's grad show exude individuality and character, similarly to the music they were derived from.
        </Para>
      </section>
      <LilSpace />  
      <WideImg src="/assets/shot‑024091.png" alt="Runway wide" />
      <LilSpace />  

      <Sub>"THE BASSLINE IS BLUE" – RICHARD BUTLER</Sub>
      
      {/* conclusion */}
      <section>
        <Para >
        I asked Butler, "How many times do you listen to a certain piece when creating the garment it's showing you?". To which he responded, "If a song is going to generate images in my head, it’ll show them to me the first time I hear it. When I press repeat, its because I want to see the image again and again," making it clear that the images do not change; whatever the image is that displays itself to the designer will remain the same if he listens to it now, later, or even in a few years on from now. It is important to note, however, that the full garments don't come into view when listening to a sound; rather, they are shapes, colours, and textures, sometimes all combined into one. The garments come from "a lot of experimentation and studying. I couldn’t put a number on how many times I’d need to listen to a piece of music to come up with a design," Butler explains. "Look, 1 was inspired by the song Gimme More. Gimme More for me is pink and black and features these big chords that glide across the sonic landscape," he described. Another example would be the song, Get Back (Main Version), which inspired his 6th look of the collection. "The song is built around a 2-bar bass line that is repeated throughout the song, and the sound used is a square synth bass," he elaborated. "The synth also has an added vibrato effect, causing the pitch to wobble up and down. In my head, the sound sits in the middle of the sonic landscape and is an obviously blue, three-dimensional geometric shape that mutates with the pitch, whilst the other multicoloured sounds dance around it on the periphery." A cacophony of symbols, coming together to dance in Butler's mind, allowing him, as a designer, to craft what he sees. These sonic shapes and colours don't remain confined to Butler's imagination, but manifest in the stylistic traits of his garments and the rhythm of their silhouettes.
        </Para>
        <Para>
        But it wasn't just Spears that fuelled his desires for experimentation, Butler also found himself inspired by the work of Oskar Fischinger, an animator on Disney's Fantasia, a childhood favourite of Butler's. Describing him as "an experimental animator who created abstract sequences to accompany music," he described, saying that Fischinger's style equally influenced the "technical construction of my collection." Butler recounted a specific technique Fichinger had used to create his morphous art, "painting images onto sheets of acetate and photographing them repeatedly. For each photo he took, he would add another brush stroke so that when the photos were collated to create a cartoon, it would look as if the brush strokes were growing and moving," something so evident in the layering of material and colours seen throughout Butler's looks. Wanting to capture this animation-like sequence through the art of dance, RIFF’s photoshoot has the pieces shot in motion, swirling, swivelling, and in mid-air, in an attempt to capture the designer’s synesthesia, at the same time paying homage to Fichinger’s animation, as one of the biggest influences on this series of work. 
        </Para>
        <Para>
        Another technique of Fichinger's was to obtain "a block of multicoloured wax" and slice it, photographing each segment, which, throughout making his way through the block, would mutate and change with the patterns collected in each slice. Butler's fourth look was inspired by the song “Get Naked”. "During the chorus of this song, the vocals have been pitched down and twisted as if they’re melting, and similarly, the synthesisers seem to have the consistency of slime (or melting wax)."
        </Para>
      </section>
      <LilSpace />  
      <ImgRow>
        <WideImg src="/assets/shot‑042742.png" alt="Runway wide 1" />
        <WideImg src="/assets/shot‑040302.png" alt="Runway wide 2" />
      </ImgRow>

      <LilSpace />   
            {/* conclusion */}
            <section>
        <Para >
        "The song is explicitly sexual, yet to me not sexy at all," he said, describing how the distorted wails of the lowered vocals give the tune a "depraved sound," something the designer redeveloped by creating an "oversized, multicoloured human head emerging from the body, engulfing the model."<br />
        </Para>
        <br />
        <Para >
        "The album is also often thought of in connection with her public breakdown and I was also experiencing a lot of the shitty things that gay children experience. When I was researching synesthesia, I was struck by how much it is connected to empathy and emotions. Blackout and the images it created in my head became like a secret garden that I used to escape to as a trauma response," stated Butler.
Revealing that there is more to the clothes than just their appearance, not only could he envision their designs in advance, but Butler also treated them as an homage to his past, drawing strength from a pivotal moment in his career that has now led to new and exciting opportunities, such as Bjork donning his graduate designs.<br />
        </Para>
        <br />
        <Para >
        It's true, Richard Butler imagines his designs to the sound of music, composing them across the vast plains of his sonic landscape. He draws inspiration from certain aspects of each track from 'Blackout', translating rhythm and tone into textured, colourful shapes that spark the initial concept for his garments. His "visualiser" is his mind: involuntary and wonderfully vivid. The music that surrounds this remarkable designer fuels his creativity and results in pieces that, much like a catchy song, linger in your memory and refuse to be forgotten, keeping Richard Butler on repeat.

        </Para>
      </section>

      <SpotifyFrame
        src="https://open.spotify.com/embed/album/1ePkYcH5ZQCb1b4tQeiEDj?utm_source=generator"
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        loading="lazy"
      />
      {/* footer */}
      <FooterBar>
        <h1>RIFF MAGAZINE</h1>
      </FooterBar>
    </Page>
  );
}
